import { Status } from '../enums/status.enum';
import { PageType } from 'src/app/static-pages/enums/page-type.enum';

/**
 * Single source of the portal navigation structure (Unified Code criteria 29, 27 and 37).
 * The header, the footer and the sitemap are all built from these pure functions, so the sitemap
 * cannot drift from what the visitor actually sees in the navigation.
 */

/** Minimal page shape shared by `GET /settings/pages` (with content) and `GET /settings/pages/active` (without). */
export interface PortalPageSummary {
  id: number;
  title: string;
  titleAr: string;
  type: PageType;
  parentId?: number | null;
  status?: Status;
  content?: string | null;
  contentAr?: string | null;
}

export interface PortalLink {
  /** Stable identifier (template tracking and tests). */
  id: string;
  /** Translation key of the label; absent when the label comes from data (`title` / `titleAr`). */
  labelKey?: string;
  title?: string;
  titleAr?: string;
  /** Internal router URL. */
  url?: string;
  /** External absolute URL: opened in a new tab and marked with the external-link icon. */
  href?: string;
  children?: PortalLink[];
}

export interface PortalLinkGroup {
  id: string;
  headingKey?: string;
  heading?: string;
  headingAr?: string;
  links: PortalLink[];
}

export interface PortalLinkContext {
  isSignedIn: boolean;
  isStudent: boolean;
  canViewDashboard: boolean;
  dashboardUrl?: string;
  showDigitalLibrary: boolean;
  userId?: number | null;
  pages: PortalPageSummary[];
}

export interface SitemapSection {
  id: string;
  headingKey: string;
  links?: PortalLink[];
  groups?: PortalLinkGroup[];
}

const LINKS = 'unifiedShell.links';

/**
 * A page is available when it is active and, if the payload carries content, has content in both
 * languages (the footer rule used today). `GET /settings/pages/active` returns only active pages
 * without status or content, so a missing field is trusted.
 */
export function isPageAvailable(page: PortalPageSummary): boolean {
  if (!page || !isActive(page)) return false;
  const carriesContent = page.content !== undefined || page.contentAr !== undefined;
  if (!carriesContent) return true;
  return (page.content?.length ?? 0) > 0 && (page.contentAr?.length ?? 0) > 0;
}

export function hasAvailablePage(pages: PortalPageSummary[], type: PageType): boolean {
  return (pages ?? []).some((page) => page.type === type && isPageAvailable(page));
}

/** Primary navigation of the header and the mobile nav drawer. */
export function buildHeaderLinks(ctx: PortalLinkContext): PortalLink[] {
  const links: PortalLink[] = [
    { id: 'home', labelKey: `${LINKS}.home`, url: '/home' },
    { id: 'courses', labelKey: `${LINKS}.courses`, url: '/course' },
  ];
  if (ctx.isStudent) links.push({ id: 'my-courses', labelKey: `${LINKS}.myCourses`, url: '/myspace/courses' });
  if (ctx.showDigitalLibrary)
    links.push({ id: 'digital-library', labelKey: `${LINKS}.digitalLibrary`, url: '/digital-library/contents' });
  if (ctx.isSignedIn && ctx.canViewDashboard && ctx.dashboardUrl)
    links.push({ id: 'dashboard', labelKey: `${LINKS}.dashboard`, href: ctx.dashboardUrl });
  return links;
}

/** One group per active custom parent page, holding its active children (the parent itself when it has none). */
export function buildCustomPageGroups(pages: PortalPageSummary[]): PortalLinkGroup[] {
  const custom = activeCustomPages(pages);
  return custom
    .filter((page) => !page.parentId)
    .map((parent) => {
      const children = custom.filter((child) => child.parentId === parent.id).map(pageLink);
      return {
        id: `custom-${parent.id}`,
        heading: parent.title,
        headingAr: parent.titleAr,
        links: children.length > 0 ? children : [pageLink(parent)],
      };
    });
}

/** Link groups of the Unified Code footer; empty groups are omitted. */
export function buildFooterGroups(ctx: PortalLinkContext): PortalLinkGroup[] {
  const pages = ctx.pages ?? [];

  const important: PortalLink[] = [
    { id: 'home', labelKey: `${LINKS}.home`, url: '/home' },
    { id: 'courses', labelKey: `${LINKS}.courses`, url: '/course' },
  ];
  if (ctx.showDigitalLibrary)
    important.push({ id: 'digital-library', labelKey: `${LINKS}.digitalLibrary`, url: '/digital-library/contents' });
  important.push(
    { id: 'about', labelKey: `${LINKS}.about`, url: '/about' },
    { id: 'faq', labelKey: `${LINKS}.faq`, url: '/platform-faq' },
    { id: 'sitemap', labelKey: `${LINKS}.sitemap`, url: '/sitemap' },
  );

  const support: PortalLink[] = [{ id: 'contact', labelKey: `${LINKS}.contact`, url: '/contact' }];
  if (hasAvailablePage(pages, PageType.UserGuide))
    support.push({ id: 'user-guide', labelKey: `${LINKS}.userGuide`, url: '/user-guide' });
  if (hasAvailablePage(pages, PageType.TraineeGuide))
    support.push({ id: 'trainee-guide', labelKey: `${LINKS}.traineeGuide`, url: '/trainee-guide' });

  const policies: PortalLink[] = [];
  if (hasAvailablePage(pages, PageType.Policy)) policies.push({ id: 'privacy', labelKey: `${LINKS}.privacy`, url: '/privacy' });
  if (hasAvailablePage(pages, PageType.Terms)) policies.push({ id: 'terms', labelKey: `${LINKS}.terms`, url: '/terms' });

  const groups: PortalLinkGroup[] = [
    { id: 'important', headingKey: 'unifiedShell.footer.importantLinks', links: important },
    { id: 'support', headingKey: 'unifiedShell.footer.supportHelp', links: support },
    { id: 'policies', headingKey: 'unifiedShell.footer.policies', links: policies },
    ...buildCustomPageGroups(pages),
  ];
  return groups.filter((group) => group.links.length > 0);
}

/** Account entry points: the sign-in flow for visitors, the personal area for signed-in users. */
export function buildAccountLinks(ctx: PortalLinkContext): PortalLink[] {
  if (!ctx.isSignedIn) {
    return [
      { id: 'login', labelKey: `${LINKS}.login`, url: '/join/login' },
      { id: 'sign-up', labelKey: `${LINKS}.signUp`, url: '/join/signUp' },
      { id: 'forgot-password', labelKey: `${LINKS}.forgotPassword`, url: '/join/forget-password' },
    ];
  }
  const links: PortalLink[] = [];
  if (ctx.userId) links.push({ id: 'profile', labelKey: `${LINKS}.mySpace`, url: `/myspace/profile/${ctx.userId}` });
  links.push({ id: 'change-password', labelKey: `${LINKS}.changePassword`, url: '/join/change-password' });
  return links;
}

/** CMS pages: the fixed pages first, then the custom parent/child hierarchy as nested links. */
export function buildPageLinks(pages: PortalPageSummary[]): PortalLink[] {
  const fixed: Array<[PageType, string, string]> = [
    [PageType.AboutUs, 'about', '/about'],
    [PageType.Policy, 'privacy', '/privacy'],
    [PageType.Terms, 'terms', '/terms'],
    [PageType.UserGuide, 'userGuide', '/user-guide'],
    [PageType.TraineeGuide, 'traineeGuide', '/trainee-guide'],
  ];
  const links: PortalLink[] = fixed
    .filter(([type]) => hasAvailablePage(pages, type))
    .map(([, key, url]) => ({ id: key, labelKey: `${LINKS}.${key}`, url }));

  const custom = activeCustomPages(pages);
  custom
    .filter((page) => !page.parentId)
    .forEach((parent) => {
      const children = custom.filter((child) => child.parentId === parent.id).map(pageLink);
      links.push({ ...pageLink(parent), children: children.length > 0 ? children : undefined });
    });
  return links;
}

/** Sitemap = header navigation + footer groups + CMS pages + account links (empty sections omitted). */
export function buildSitemap(ctx: PortalLinkContext): SitemapSection[] {
  const sections: SitemapSection[] = [
    { id: 'main', headingKey: 'unifiedShell.sitemap.mainNavigation', links: buildHeaderLinks(ctx) },
    { id: 'footer', headingKey: 'unifiedShell.sitemap.footerLinks', groups: buildFooterGroups(ctx) },
    { id: 'pages', headingKey: 'unifiedShell.sitemap.pages', links: buildPageLinks(ctx.pages) },
    { id: 'account', headingKey: 'unifiedShell.sitemap.account', links: buildAccountLinks(ctx) },
  ];
  return sections.filter((section) => (section.links?.length ?? 0) > 0 || (section.groups?.length ?? 0) > 0);
}

function isActive(page: PortalPageSummary): boolean {
  return page.status === undefined || page.status === null || page.status === Status.Active;
}

/** Custom pages follow today's footer rule (active only); a parent may be a heading without content. */
function activeCustomPages(pages: PortalPageSummary[]): PortalPageSummary[] {
  return (pages ?? []).filter((page) => page.type === PageType.Custom && isActive(page));
}

function pageLink(page: PortalPageSummary): PortalLink {
  return { id: `page-${page.id}`, title: page.title, titleAr: page.titleAr, url: `/page/${page.id}` };
}
